import React, { useState } from 'react';
import PropTypes from 'prop-types';

import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import CardMedia from '@material-ui/core/CardMedia';
import Chip from '@material-ui/core/Chip';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import ViewListIcon from '@material-ui/icons/ViewList';
import ViewModuleIcon from '@material-ui/icons/ViewModule';

import TablePaginationActions from '../Table/pagination';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
  },
  mediaWrap: {
    position: 'relative',
  },
  media: {
    height: 160,
    objectFit: 'cover',
    backgroundColor: '#f5f5f5',
  },
  sourceChip: {
    position: 'absolute',
    top: 8,
    left: 8,
  },
  title: {
    fontWeight: 600,
  },
  actions: {
    justifyContent: 'flex-end',
    paddingTop: 0,
  },
  pagination: {
    marginTop: theme.spacing(1),
  },
}));

function AssetCard({ row, getImage, getTitle, getSubtitle, getSourceLabel, actions, onImageClick }) {
  const classes = useStyles();
  const image = getImage(row) || {};
  const [src, setSrc] = useState(image.src || image.fallback || '');
  const title = getTitle(row);
  const subtitle = getSubtitle ? getSubtitle(row) : '';
  const sourceLabel = getSourceLabel ? getSourceLabel(row) : '';

  const handleError = (event) => {
    if (image.fallback && event.target.src !== image.fallback) {
      setSrc(image.fallback);
      return;
    }
    event.target.style.visibility = 'hidden';
  };

  return (
    <Card variant="outlined">
      <CardActionArea onClick={() => onImageClick && onImageClick(row)} disabled={!src}>
        <div className={classes.mediaWrap}>
          {src ? (
            <CardMedia
              className={classes.media}
              component="img"
              image={src}
              alt={image.alt || title || ''}
              onError={handleError}
            />
          ) : (
            <div className={classes.media} />
          )}
          {sourceLabel ? (
            <Chip className={classes.sourceChip} size="small" label={sourceLabel} />
          ) : null}
        </div>
      </CardActionArea>
      <CardContent>
        <Typography className={classes.title} variant="body2" noWrap title={title}>
          {title || 'Untitled'}
        </Typography>
        {subtitle ? (
          <Typography variant="caption" color="textSecondary" noWrap>
            {subtitle}
          </Typography>
        ) : null}
      </CardContent>
      {actions.length > 0 && (
        <CardActions className={classes.actions} disableSpacing>
          {actions.map((action) => (
            <Tooltip key={action.label} title={action.label}>
              <span>
                <IconButton
                  size="small"
                  aria-label={action.label}
                  disabled={action.disabled ? action.disabled(row) : false}
                  onClick={() => action.onClick(row)}
                >
                  {action.icon}
                </IconButton>
              </span>
            </Tooltip>
          ))}
        </CardActions>
      )}
    </Card>
  );
}

export default function ImageAssetView({
  data,
  table,
  handlePage,
  getImage,
  getTitle,
  getSubtitle,
  actions,
  onImageClick,
  getSourceLabel,
  viewMode: controlledMode,
  onViewModeChange,
  showToggle,
}) {
  const classes = useStyles();
  const [internalMode, setInternalMode] = useState('table');
  const viewMode = controlledMode || internalMode;
  const docs = data?.docs || [];

  const setMode = (next) => {
    if (!next) return;
    if (onViewModeChange) onViewModeChange(next);
    if (!controlledMode) setInternalMode(next);
  };

  return (
    <div>
      {showToggle && (
        <div className={classes.toolbar}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={viewMode}
            onChange={(_, value) => setMode(value)}
            aria-label="asset view"
          >
            <ToggleButton value="table" aria-label="table view">
              <ViewListIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModuleIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      )}

      {viewMode === 'table' ? table : (
        <>
          <Grid container spacing={2}>
            {docs.map((row) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={row._id}>
                <AssetCard
                  row={row}
                  getImage={getImage}
                  getTitle={getTitle}
                  getSubtitle={getSubtitle}
                  getSourceLabel={getSourceLabel}
                  actions={actions}
                  onImageClick={onImageClick}
                />
              </Grid>
            ))}
          </Grid>
          {data?.totalDocs > 0 && (
            <div className={classes.pagination}>
              <TablePaginationActions
                count={data.totalDocs}
                limit={data.limit}
                totalPages={data.totalPages}
                page={data.page}
                hasNext={data.hasNextPage}
                hasPrev={data.hasPrevPage}
                nextPage={data.nextPage}
                prevPage={data.prevPage}
                onChangePage={handlePage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

ImageAssetView.propTypes = {
  data: PropTypes.object,
  table: PropTypes.node,
  handlePage: PropTypes.func,
  getImage: PropTypes.func.isRequired,
  getTitle: PropTypes.func.isRequired,
  getSubtitle: PropTypes.func,
  actions: PropTypes.array,
  onImageClick: PropTypes.func,
  getSourceLabel: PropTypes.func,
  viewMode: PropTypes.string,
  onViewModeChange: PropTypes.func,
  showToggle: PropTypes.bool,
};

ImageAssetView.defaultProps = {
  actions: [],
  showToggle: true,
  handlePage: () => {},
};
